import { useState } from "react";

import { useNavigate, useParams } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, FileText, Search } from "lucide-react";

import { NAVIGATION_PATHS } from "@constants/navigation";
import { getServiceTypeLabel } from "@constants/service-types";

import { useCaseByCaseNumber } from "@hooks/queries/use-cases";

import { formatNullableDateToLocal } from "@utils/dateUtils";
import { cn } from "@utils/ui";

// Mock expert suggestions (design uses illustrative data; real find-an-expert is
// a later, separate build). worked = "Previously worked with".
const MOCK_EXPERTS = [
  {
    initials: "SO",
    name: "Dr Samuel Okafor",
    specialty: "Psychiatry",
    meta: "Melbourne, VIC · Telehealth",
    rating: "4.8",
    turnaround: "12d turnaround",
    worked: true,
  },
  {
    initials: "SM",
    name: "Dr Sophie Mercer",
    specialty: "Psychiatry",
    meta: "Brisbane, QLD · In-person / Telehealth",
    rating: "4.7",
    turnaround: "9d turnaround",
    worked: true,
  },
  {
    initials: "AL",
    name: "Dr Aisha Lund",
    specialty: "Occupational Medicine",
    meta: "Sydney, NSW · In-person",
    rating: "4.9",
    turnaround: "7d turnaround",
    worked: false,
  },
];

const APPT_FILTERS = ["All", "Previously worked with", "New to you"] as const;
type ApptFilter = (typeof APPT_FILTERS)[number];

// ── design-matched card primitives ───────────────────────────────────────────
const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-[18px] overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
    {children}
  </div>
);

const CardHead = ({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 border-b border-[#eef1f6] px-6 py-[18px]">
    <div className="text-base font-bold text-[#1A1939]">{title}</div>
    {action}
  </div>
);

const Row = ({
  label,
  value,
  labelW = "w-[150px]",
  full = false,
}: {
  label: string;
  value: React.ReactNode;
  labelW?: string;
  full?: boolean;
}) => (
  <div className={cn("flex gap-2.5", full && "md:col-span-2")}>
    <span className={cn("flex-none text-[13px] text-[#8A8A98]", labelW)}>
      {label}
    </span>
    <span className="text-[13px] font-medium text-[#1A1939]">{value}</span>
  </div>
);

const bodyGrid = "grid grid-cols-1 gap-x-10 gap-y-3.5 px-6 py-5 md:grid-cols-2";

const CaseSummaryPage = () => {
  const { caseNumber } = useParams({ strict: false }) as { caseNumber: string };
  const navigate = useNavigate();
  const [tab, setTab] = useState<"details" | "appointment">("details");
  const [expertSearch, setExpertSearch] = useState("");
  const [apptFilter, setApptFilter] = useState<ApptFilter>("All");

  const { data: c, isLoading } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const dash = (v?: string | null) => (v && v.trim() ? v : "—");
  const yesNo = (v?: boolean | null) =>
    v === true ? "Yes" : v === false ? "No" : "—";
  const svc = (v?: string | null) => (v ? getServiceTypeLabel(v) || v : "—");
  const date = (v?: string | null) => formatNullableDateToLocal(v) || "—";

  const experts = MOCK_EXPERTS.filter(
    (x) =>
      (apptFilter === "All" ||
        (apptFilter === "Previously worked with" && x.worked) ||
        (apptFilter === "New to you" && !x.worked)) &&
      x.name.toLowerCase().includes(expertSearch.toLowerCase())
  );

  return (
    <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px]">
      <div className="mx-auto max-w-[1180px]">
        <button
          type="button"
          onClick={() => navigate({ to: NAVIGATION_PATHS.CASES })}
          className="mb-4 flex items-center gap-[7px] text-[13px] font-semibold text-[#0065CB]"
        >
          <ChevronLeft className="h-[15px] w-[15px]" /> Back to cases
        </button>

        <div className="mb-4">
          <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1A1939]">
            Case summary
          </h1>
          {c && (
            <div className="text-[13.5px] text-[#5A5A6B]">
              {dash(c.claim_number || c.case_number)} · {dash(c.examinee_name)} ·{" "}
              {svc(c.service_type)}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-5 flex gap-1 border-b border-[#E2E5EB]">
          {(["details", "appointment"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "-mb-px border-b-2 px-[18px] py-2.5 text-[13.5px] font-semibold",
                tab === t
                  ? "border-[#0065CB] text-[#0065CB]"
                  : "border-transparent text-[#5A5A6B]"
              )}
            >
              {t === "details" ? "Case details" : "Appointment"}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-sm text-[#8A8A98]">Loading…</div>
        ) : !c ? (
          <div className="py-16 text-center text-sm text-[#8A8A98]">
            Case not found.
          </div>
        ) : tab === "details" ? (
          <>
            <Card>
              <CardHead title="Case overview" />
              <div className={bodyGrid}>
                <Row label="Claimant name" value={dash(c.examinee_name)} />
                <Row label="Doctor" value="Not yet assigned" />
                <Row label="Referrer" value="You" />
                <Row label="Referrer organisation" value={dash(c.organisation)} />
                <Row label="Case type" value="Referral" />
                <Row label="Service type" value={svc(c.service_type)} />
                <Row label="Specialty required" value={dash(c.specialty)} />
                <Row label="Your sector" value={dash(c.jurisdiction)} />
                <Row label="Your state" value={dash(c.jurisdiction)} />
              </div>
            </Card>

            <Card>
              <CardHead
                title="Injured worker details"
                action={
                  <button
                    type="button"
                    className="rounded-[9px] bg-[#1FA37A] px-4 py-2 text-[13px] font-semibold text-white"
                  >
                    Secure &amp; Switch
                  </button>
                }
              />
              <div className={bodyGrid}>
                <Row label="Name" value={dash(c.examinee_name)} labelW="w-[130px]" />
                <Row label="Date of birth" value={date(c.date_of_birth)} />
                <Row
                  label="Mobile"
                  value={dash(c.claimant_mobile)}
                  labelW="w-[130px]"
                />
                <Row label="Email address" value={dash(c.claimant_email)} />
              </div>
            </Card>

            <Card>
              <CardHead title="Employer details" />
              <div className="px-6 py-5">
                <Row
                  label="Claimant's employer name"
                  value={dash(c.employer_name)}
                  labelW="w-[170px]"
                />
              </div>
            </Card>

            <Card>
              <CardHead title="Case details" />
              <div className={bodyGrid}>
                <Row
                  label="Claim no."
                  value={dash(c.claim_number)}
                  labelW="w-[130px]"
                />
                <Row label="Date of injury" value={date(c.date_of_injury)} />
                <Row
                  label="Interpreter required"
                  value={yesNo(c.interpreter_required)}
                  labelW="w-[130px]"
                />
                <Row label="Urgent report" value={yesNo(c.urgent_report)} />
                <Row
                  label="Date report is due"
                  value={date(c.report_due_date)}
                  labelW="w-[130px]"
                />
                <Row
                  label="Report addressed to you"
                  value={yesNo(c.report_addressed_to_referrer)}
                />
                <Row
                  label="Examinee attendance"
                  value="Not yet confirmed"
                  labelW="w-[130px]"
                />
                <Row label="Appointment" value={date(c.appointment_date)} />
                <Row
                  label="Comments"
                  value={dash(c.comments)}
                  labelW="w-[130px]"
                  full
                />
                <div className="flex items-start gap-2.5 md:col-span-2">
                  <span className="w-[130px] flex-none text-[13px] text-[#8A8A98]">
                    Invoice details
                  </span>
                  <div className="rounded-[10px] border border-[#eef1f6] bg-[#F5F7FA] px-4 py-3.5 text-[13px] leading-[1.7] text-[#5A5A6B]">
                    {dash(c.organisation)}
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <CardHead title="Documentation" />
              <div className="px-6 py-5">
                <div className="flex items-start gap-2.5 rounded-[10px] border border-[#E2E5EB] p-4">
                  <FileText className="mt-px h-[18px] w-[18px] flex-none text-[#0065CB]" />
                  <div className="flex-1">
                    <div className="text-[13.5px] font-semibold text-[#1A1939]">
                      Request documents from a provider
                    </div>
                    <div className="mt-0.5 text-xs text-[#8A8A98]">
                      We&apos;ll request the records directly (SureMed) and attach
                      them to this case when they arrive.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        to: `${NAVIGATION_PATHS.REPORT_STUDIO}/${c.case_number}/request-documents`,
                      })
                    }
                    className="flex-none rounded-[9px] border border-[#cdddfb] bg-white px-4 py-2 text-[12.5px] font-semibold text-[#0065CB]"
                  >
                    Request documents
                  </button>
                </div>
              </div>
            </Card>
          </>
        ) : (
          <>
            {/* Appointment — unbooked */}
            <Card>
              <div className="flex items-center gap-4 p-6">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[12px] bg-[#fdf7ec] text-[#B26A00]">
                  <CalendarDays className="h-[22px] w-[22px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-[#1A1939]">
                    No appointment booked yet
                  </div>
                  <div className="mt-[3px] text-[13px] text-[#5A5A6B]">
                    Book when you&apos;re ready — search below for a medical expert.
                    Experts you&apos;ve worked with before appear first.
                  </div>
                </div>
              </div>
            </Card>

            {/* Find a medical expert */}
            <Card>
              <div className="border-b border-[#eef1f6] px-6 py-[18px]">
                <div className="text-base font-bold text-[#1A1939]">
                  Find a medical expert
                </div>
                <div className="mt-[3px] text-[12.5px] text-[#8A8A98]">
                  Suggestions for {dash(c.specialty)} needed for this case. Experts
                  you&apos;ve previously worked with appear first.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-6 pb-1.5 pt-4">
                <div className="relative flex min-w-[220px] max-w-[420px] flex-1 items-center">
                  <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#8A8A98]" />
                  <input
                    value={expertSearch}
                    onChange={(e) => setExpertSearch(e.target.value)}
                    placeholder="Search medical experts"
                    className="w-full rounded-[10px] border border-[#E2E5EB] py-[9px] pl-[34px] pr-3 text-[13.5px] outline-none focus:border-[#0065CB]"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {APPT_FILTERS.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setApptFilter(f)}
                      className={cn(
                        "whitespace-nowrap rounded-full border px-[13px] py-2 text-[12.5px] font-semibold",
                        apptFilter === f
                          ? "border-[#0065CB] bg-[#0065CB] text-white"
                          : "border-[#E2E5EB] bg-white text-[#5A5A6B]"
                      )}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="py-1.5">
                {experts.map((e) => (
                  <div
                    key={e.name}
                    className="flex items-center gap-3.5 border-b border-[#f4f6fa] px-6 py-[13px]"
                  >
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-gradient-to-br from-[#0065CB] to-[#00D8D8] text-[13px] font-bold text-white">
                      {e.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13.5px] font-semibold text-[#1A1939]">
                          {e.name}
                        </span>
                        {e.worked && (
                          <span className="rounded-full bg-[#e7f6ec] px-[9px] py-0.5 text-[10.5px] font-bold text-[#16794f]">
                            Previously worked with
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-[#8A8A98]">
                        {e.specialty} · {e.meta}
                      </div>
                    </div>
                    <div className="flex-none whitespace-nowrap text-[11.5px] text-[#5A5A6B]">
                      ★ {e.rating} · {e.turnaround}
                    </div>
                    <button
                      type="button"
                      className="flex-none rounded-[9px] border border-[#cdddfb] bg-white px-[15px] py-2 text-[12.5px] font-semibold text-[#0065CB]"
                    >
                      {e.worked ? "Book again" : "Select"}
                    </button>
                  </div>
                ))}
                {experts.length === 0 && (
                  <div className="px-6 py-[22px] text-center text-[13px] text-[#8A8A98]">
                    No experts match your search.
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default CaseSummaryPage;
